import { useState } from 'react';
import {
	Combobox,
	ComboboxInput,
	ComboboxButton,
	ComboboxOptions,
	ComboboxOption,
} from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface ComboBoxProps {
	label?: string;
	/** Current value (free text or one of the options) */
	value: string;
	onChange: (value: string) => void;
	/** Predefined suggestions shown on focus and filtered as the user types */
	options: string[];
	placeholder?: string;
	error?: string;
	helperText?: string;
	required?: boolean;
	disabled?: boolean;
	className?: string;
}

/**
 * Free-text combobox: the user can type any string OR pick from a predefined
 * list of suggestions. The full list opens on focus and filters as they type.
 */
const ComboBox = ({
	label,
	value,
	onChange,
	options,
	placeholder,
	error,
	helperText,
	required,
	disabled,
	className = '',
}: ComboBoxProps) => {
	const [query, setQuery] = useState('');

	const normalized = query.trim().toLowerCase();
	const filtered =
		normalized === '' ? options : options.filter(o => o.toLowerCase().includes(normalized));

	return (
		<div className="w-full">
			{label && (
				<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
					{label}
					{required && <span className="text-red-500 ml-1">*</span>}
				</label>
			)}

			<Combobox
				value={value}
				onChange={(v: string | null) => onChange(v ?? '')}
				disabled={disabled}
				immediate
			>
				<div className="relative">
					<ComboboxInput
						className={`w-full px-3 py-2.5 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all
							focus:outline-none focus:border-blue-600 focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900
							disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed
							${error ? 'border-red-500 focus:border-red-500 focus:ring-red-100 dark:focus:ring-red-900' : ''}
							${className}`}
						placeholder={placeholder}
						displayValue={(v: string) => v}
						onChange={e => {
							setQuery(e.target.value);
							// Keep the typed text as the value so free-text entries are preserved
							onChange(e.target.value);
						}}
					/>
					<ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2.5 cursor-pointer">
						<ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
					</ComboboxButton>

					<ComboboxOptions
						transition
						className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none text-sm origin-top
							transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95"
					>
						{filtered.length === 0 ? (
							<div className="px-3 py-2 text-gray-500 dark:text-gray-400">
								{normalized === ''
									? 'Start typing…'
									: `No preset match — “${query.trim()}” will be used`}
							</div>
						) : (
							filtered.map(option => (
								<ComboboxOption
									key={option}
									value={option}
									className="group flex items-center justify-between px-3 py-2 cursor-pointer text-gray-700 dark:text-gray-200 data-[focus]:bg-blue-50 dark:data-[focus]:bg-blue-900/30 data-[focus]:text-blue-700 dark:data-[focus]:text-blue-300"
								>
									<span>{option}</span>
									<CheckIcon className="w-4 h-4 opacity-0 group-data-[selected]:opacity-100 text-blue-600 dark:text-blue-400" />
								</ComboboxOption>
							))
						)}
					</ComboboxOptions>
				</div>
			</Combobox>

			{error && <span className="form-error text-red-500 text-xs mt-1 block">{error}</span>}
			{helperText && !error && (
				<span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
					{helperText}
				</span>
			)}
		</div>
	);
};

export default ComboBox;
